import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { useRequest } from '@frontend/api';
import type {
  QueryFunction,
  MutationFunction,
  RequestQueryObject,
} from '@frontend/api';
import { useToast } from '@frontend/components';

import type {
  LinearNestingDefaultsData,
  SheetNestingDefaultsData,
} from 'modules/common/components/nestingDrawers';

import { drawer } from '.';

// TODO: move all of this to API package
export type NestingDefaultsRequestParams = {
  locator: string;
};

export type NestingDefaultsQueryKey = readonly [
  'nesting',
  string,
  NestingDefaultsRequestParams,
];
export const getSheetNestingDefaultsQueryKey = (
  params: NestingDefaultsRequestParams,
): NestingDefaultsQueryKey => ['nesting', `sheetMetalNestingDefaults`, params];
export const getLinearNestingDefaultsQueryKey = (
  params: NestingDefaultsRequestParams,
): NestingDefaultsQueryKey => ['nesting', `linearNestingDefaults`, params];

export type NestingDefaultsMutationKey = readonly [
  'nesting',
  string,
  NestingDefaultsRequestParams,
  'update',
];
export const getSheetNestingDefaultsMutationKey = (
  params: NestingDefaultsRequestParams,
): NestingDefaultsMutationKey => [
  'nesting',
  'sheetMetalNestingDefaults',
  params,
  'update',
];
export const getLinearNestingDefaultsMutationKey = (
  params: NestingDefaultsRequestParams,
): NestingDefaultsMutationKey => [
  'nesting',
  'linearNestingDefaults',
  params,
  'update',
];

export const useSheetNestingDefaults: QueryFunction<
  SheetNestingDefaultsData,
  [NestingDefaultsRequestParams],
  NestingDefaultsQueryKey
> = (params, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryKey = getSheetNestingDefaultsQueryKey(params);

  return useQuery({
    ...queryOptions,
    queryKey,
    queryFn: ({ signal }) => {
      return request(
        `/quotes/nesting/${params.locator}/sheet_metal_nesting_defaults`,
        {
          init: {
            method: 'GET',
            signal,
          },
        },
      );
    },
  });
};

export const useLinearNestingDefaults: QueryFunction<
  LinearNestingDefaultsData,
  [NestingDefaultsRequestParams],
  NestingDefaultsQueryKey
> = (params, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryKey = getLinearNestingDefaultsQueryKey(params);

  return useQuery({
    ...queryOptions,
    queryKey,
    queryFn: ({ signal }) => {
      return request(
        `/quotes/nesting/${params.locator}/linear_nesting_defaults`,
        {
          init: {
            method: 'GET',
            signal,
          },
        },
      );
    },
  });
};

export const useUpdateSheetNestingDefaults: MutationFunction<
  Partial<SheetNestingDefaultsData>,
  Partial<SheetNestingDefaultsData>,
  RequestQueryObject,
  [NestingDefaultsRequestParams]
> = (params, { requestOptions, queryOptions = {} } = {}) => {
  const queryClient = useQueryClient();

  const { locator } = params;
  const urlPath = `/quotes/nesting/${locator}/sheet_metal_nesting_defaults`;
  const mutationKey = getSheetNestingDefaultsMutationKey(params);
  const request = useRequest(requestOptions);
  const toast = useToast();
  const dispatch = useDispatch();

  return useMutation(
    (requestData) => {
      return request(urlPath, {
        init: {
          method: 'PATCH',
          body: requestData,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getSheetNestingDefaultsQueryKey(params));
        toast({
          status: 'success',
          title: 'Updated sheet nesting default values',
        });
        dispatch(drawer.close());
      },
      onError: (e) => {
        toast({
          status: 'error',
          title: 'Failed to update sheet nesting default values',
          description: 'Check your internet connection and try again.',
        });
        console.error(e);
      },
      ...queryOptions,
      mutationKey,
    },
  );
};

export const useUpdateLinearNestingDefaults: MutationFunction<
  Partial<LinearNestingDefaultsData>,
  Partial<LinearNestingDefaultsData>,
  RequestQueryObject,
  [NestingDefaultsRequestParams]
> = (params, { requestOptions, queryOptions = {} } = {}) => {
  const queryClient = useQueryClient();

  const { locator } = params;
  const urlPath = `/quotes/nesting/${locator}/linear_nesting_defaults`;
  const mutationKey = getLinearNestingDefaultsMutationKey(params);
  const request = useRequest(requestOptions);
  const toast = useToast();
  const dispatch = useDispatch();

  return useMutation(
    (requestData) => {
      return request(urlPath, {
        init: {
          method: 'PATCH',
          body: requestData,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getLinearNestingDefaultsQueryKey(params));
        toast({
          status: 'success',
          title: 'Updated linear nesting default values',
        });
        dispatch(drawer.close());
      },
      onError: (e) => {
        toast({
          status: 'error',
          title: 'Failed to update linear nesting default values',
          description: 'Check your internet connection and try again.',
        });
        console.error(e);
      },
      ...queryOptions,
      mutationKey,
    },
  );
};
